import { FieldMessage, fieldDescription } from './TextField';
import { FloatingList } from './FloatingList';
import { uiTexts } from './texts';
import { useServerFieldError } from './fieldErrors';
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  group?: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  label?: ReactNode;
  options: SelectOption[];
  placeholder?: string;
  hint?: ReactNode;
  /** Replaces the hint and marks the control invalid; a `name` inside a `FormDialog` gets the server's message. */
  error?: ReactNode;
  /** Class of the wrapping `.nyk-ui-field` (grid placement); `className` goes to the control. */
  fieldClassName?: string;
  onChange?: (value: string) => void;
  /** Type to filter the options; default on from {@link SEARCHABLE_FROM} options. */
  searchable?: boolean;
}

/** Lists this long get a text field that filters them. */
export const SEARCHABLE_FROM = 12;

/** Native select (optgroups by `group`); with `label` it is linked by id so the hint never joins the accessible name. */
export function Select({
  label,
  options,
  placeholder,
  hint,
  error: ownError,
  fieldClassName,
  onChange,
  className,
  id,
  searchable,
  ...rest
}: SelectProps) {
  const auto = useId();
  const controlId = id ?? auto;
  const server = useServerFieldError(rest.name);
  const error = ownError ?? server;
  // Hint and error describe the control only where they are shown (with a label).
  const ids = fieldDescription(
    controlId,
    label ? hint : undefined,
    label ? error : undefined,
    rest['aria-describedby']
  );
  const invalid = rest['aria-invalid'] ?? (error ? true : undefined);
  const field = (control: ReactNode) =>
    label ? (
      <div className={['nyk-ui-field', fieldClassName].filter(Boolean).join(' ')}>
        <label htmlFor={controlId}>{label}</label>
        {control}
        <FieldMessage hint={hint} error={error} ids={ids} />
      </div>
    ) : (
      control
    );
  if (searchable ?? options.length >= SEARCHABLE_FROM)
    return field(
      <SearchSelect
        id={label ? controlId : id}
        className={className}
        options={options}
        placeholder={placeholder}
        onChange={onChange}
        value={rest.value == null ? '' : String(rest.value)}
        disabled={rest.disabled}
        ariaLabel={rest['aria-label']}
        describedBy={ids.describedBy}
        testId={(rest as Record<string, unknown>)['data-testid'] as string | undefined}
        title={rest.title}
        required={rest.required}
        invalid={invalid}
      />
    );
  const groups = new Map<string | undefined, SelectOption[]>();
  for (const o of options) groups.set(o.group, [...(groups.get(o.group) ?? []), o]);
  const render = (list: SelectOption[]) =>
    list.map((o) => (
      <option key={o.value} value={o.value} disabled={o.disabled}>
        {o.label}
      </option>
    ));
  const select = (
    <select
      id={label ? controlId : id}
      className={['nyk-ui-select', className].filter(Boolean).join(' ')}
      onChange={(e) => onChange?.(e.target.value)}
      {...rest}
      aria-describedby={ids.describedBy}
      aria-invalid={invalid}
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {[...groups.entries()].map(([g, list]) =>
        g ? (
          <optgroup key={g} label={g}>
            {render(list)}
          </optgroup>
        ) : (
          render(list)
        )
      )}
    </select>
  );
  return field(select);
}

const norm = (s: string) => s.toLocaleLowerCase();

/**
 * A combobox for long lists: shows the chosen label, typing filters by label, value or group (contains, any case);
 * arrows + Enter pick, Escape closes. A typed text that leaves exactly one option equal to it picks that option.
 */
function SearchSelect({
  id,
  className,
  options,
  placeholder,
  onChange,
  value,
  disabled,
  ariaLabel,
  describedBy,
  testId,
  title,
  required,
  invalid,
}: {
  id?: string;
  className?: string;
  options: SelectOption[];
  placeholder?: string;
  onChange?: (value: string) => void;
  value: string;
  disabled?: boolean;
  ariaLabel?: string;
  describedBy?: string;
  testId?: string;
  title?: string;
  /** Required by the chosen value, not the text in the input (a typed query or a placeholder is no choice). */
  required?: boolean;
  invalid?: boolean | 'true' | 'false' | 'grammar' | 'spelling';
}) {
  const listId = useId();
  const input = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState<string | null>(null);
  const [active, setActive] = useState(-1);
  const open = query !== null;
  const selected = options.find((o) => o.value === value);
  const shown = useMemo(() => {
    const q = norm(query ?? '').trim();
    return q
      ? options.filter((o) => [o.label, o.value, o.group ?? ''].some((t) => norm(t).includes(q)))
      : options;
  }, [options, query]);
  const pickable = (i: number) => i >= 0 && i < shown.length && !shown[i].disabled;
  useEffect(() => {
    input.current?.setCustomValidity(required && !value ? uiTexts.required() : '');
  }, [required, value]);

  const close = () => {
    setQuery(null);
    setActive(-1);
  };
  const pick = (o: SelectOption) => {
    close();
    if (o.value !== value) onChange?.(o.value);
  };
  const move = (step: 1 | -1) => {
    if (!shown.length) return;
    let i = active;
    for (let n = 0; n < shown.length; n++) {
      i = (i + step + shown.length) % shown.length;
      if (pickable(i)) break;
    }
    setActive(i);
    document.getElementById(`${listId}-${i}`)?.scrollIntoView?.({ block: 'nearest' });
  };
  const keys = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) setQuery('');
      move(e.key === 'ArrowDown' ? 1 : -1);
    } else if (e.key === 'Enter' && open) {
      e.preventDefault();
      const i = pickable(active) ? active : shown.findIndex((o) => !o.disabled);
      if (i >= 0) pick(shown[i]);
    } else if (e.key === 'Escape' && open) {
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  };

  let lastGroup: string | undefined;
  return (
    <div
      ref={box}
      className={['nyk-ui-combo', className].filter(Boolean).join(' ')}
      data-open={open ? '' : undefined}
    >
      <input
        ref={input}
        id={id}
        className="nyk-ui-select nyk-ui-combo-input"
        role="combobox"
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && pickable(active) ? `${listId}-${active}` : undefined}
        data-testid={testId}
        data-value={value}
        title={title}
        disabled={disabled}
        autoComplete="off"
        spellCheck={false}
        placeholder={selected?.label ?? placeholder}
        value={open ? query : (selected?.label ?? '')}
        onFocus={() => setQuery('')}
        onClick={() => !open && setQuery('')}
        onBlur={close}
        onKeyDown={keys}
        onChange={(e) => {
          const text = e.target.value;
          const q = norm(text).trim();
          const hits = options.filter(
            (o) => [o.label, o.value].some((t) => norm(t).includes(q)) && !o.disabled
          );
          if (hits.length === 1 && (hits[0].value === text || hits[0].label === text)) return pick(hits[0]);
          setQuery(text);
          setActive(-1);
        }}
      />
      {open && (
        <FloatingList
          anchor={box}
          id={listId}
          role="listbox"
          aria-label={ariaLabel}
          onMouseDown={(e) => e.preventDefault()}
        >
          {!shown.length && <div className="nyk-ui-combo-empty">{uiTexts.noMatch()}</div>}
          {shown.map((o, i) => {
            const heading = o.group && o.group !== lastGroup ? o.group : null;
            lastGroup = o.group;
            return (
              <div key={o.value}>
                {heading && <div className="nyk-ui-combo-group">{heading}</div>}
                <div
                  id={`${listId}-${i}`}
                  role="option"
                  className="nyk-ui-combo-opt"
                  aria-selected={o.value === value}
                  aria-disabled={o.disabled || undefined}
                  data-active={i === active ? '' : undefined}
                  onMouseEnter={() => !o.disabled && setActive(i)}
                  onClick={() => !o.disabled && pick(o)}
                >
                  {o.label}
                </div>
              </div>
            );
          })}
        </FloatingList>
      )}
    </div>
  );
}
